import {action, autorun, computed, makeObservable, observable, reaction} from "mobx";
import stores from "./stores/index.js";

import {Backend} from "./backend/index.js";
import {apiConfig} from "./backend/api/index.js";
import ApiBackend from "./backend/api/api-comm.js";
import ParentBackend, {parentBackendRequest} from "./backend/parent/index.js";
import DummyBackend from "./backend/dummy/index.js";
import Self from "./backend/self/index.js";

import {Sync, outSyncHandler, setupSyncHandlers, setupOutSyncHandlers} from "./sync/index.js";
import WsSync from "./sync/ws/index.js";
import ParentSync from "./sync/parent/index.js";

import Storage, {StorageChange} from "./storage/index.js";
import ExtLocalStorage from "./storage/ext-local-storage.js";
import Mv2ExtLocalStorage from "./storage/mv2-ext-local-storage.js";
import WindowLocalStorage from "./storage/window-local-storage.js";

import {
	account, accountUuid, accessTokenUuid,
	project, projectUuid,
	topic,
	annotation, annotationUuid,
	notification, mergedOutline2
} from "./model/index.js";
import {AppConfigStore} from "./stores/app-config.js";
import Data from "./index.js";
import {waitUntil, retryFunctionUntil} from "../utils/async.js";
import {STORAGE_CURRENT_PROJECT_UUID} from "../constants/general.js";
import Project from "../project.js";

export interface config {
	backend: "api" | "parent" | null;
	sync:    "ws" | "parent" | null;
	storage: "extLocalStorage" | "mv2ExtLocalStorage" | "windowLocalStorage" | null;
	urls: {
		promo:           string;
		pn_www:          string;
		pn_api:          string;
		pn_comments_api: string;
		pn_files_api:    string;
		pn_auth_ws:      string;
	};
	userAgent: string;
	client:    string; // e.g. "ext"
	version:   string;
	settings?: {
		showReadOnlyProjects?: boolean;
	};
};

type backendErrorHandler = (error: any, ctx: any) => void;
type authFailHandler = () => void;

export interface extraConfig {
	backendErrorHandler?:    backendErrorHandler;
	parentBackendRequestFn?: parentBackendRequest; // required when backend is "parent"
	outSyncHandler?:         outSyncHandler; // outward sync (e.g. from background.js to content-script.js)
	authFailHandler?:        authFailHandler; // called when we receive a 401 from the API or the appropriate message from sync
};

class App {
	config:  config | null = null;
	backend: Backend; // e.g. API, or another instance of this data layer
	storage: Storage | null; // e.g. the browser's local storage
	sync:    Sync | null;
	self:    Backend; // uses the same interface as backend, but tries to do things without calling the backend

	backendErrorHandler?: backendErrorHandler;
	_authFailHandler?:    authFailHandler;

	isLoggingIn = false;
	accountUuid:            accountUuid | null = null;
	accessToken:            accessTokenUuid | null = null;
	pageUrl:                string | null = null;
	unrenderableHighlights: annotationUuid[] = [];
	_projectUuid:           projectUuid | null = null;

	constructor() {
		makeObservable(this, {
			config:                        observable,
			isLoggingIn:                   observable,
			accountUuid:                   observable,
			accessToken:                   observable,
			unrenderableHighlights:        observable,
			pageUrl:                       observable,
			setCurrentProject:             action,
			setCurrentPage:                action,
			init:                          action,
			login:                         action,
			logout:                        action,
			createIntegrationAccount:      action,
			integrationSignup:             action,
			unmarkHighlightAsUnrenderable: action,
			markHighlightAsUnrenderable:   action,
			setRenderedHighlights:         action,
			isLoggedIn:                    computed,
			me:                            computed,
			project:                       computed,
			outline:                       computed,
			topics:                        computed,
			notifications:                 computed,
			pageHighlights:                computed,
			bookmarkUuid:                  computed,
			hasBackend:                    computed,
			hasStorage:                    computed,
			acceptableProjects:            computed,
			_projectUuid:                  observable,
			_clearAllData:                 action,
			_refreshAllData:               action
		});

		this.self = new Self();

		reaction(() => this.isLoggedIn, (isLoggedIn: boolean) => {
			if (!isLoggedIn)
				this._clearAllData();
		});

		reaction(() => ({
			backend:     this.backend,
			accessToken: this.accessToken
		}), ({backend, accessToken}: {backend: Backend; accessToken: accessTokenUuid}) => {
			if (backend instanceof ApiBackend && accessToken)
				apiConfig.accessToken = accessToken;
		});

		// Select a different project when the current one is removed from the list of acceptable ones
		// (e.g. deleted, archived, share access changed to readonly).
		autorun(() => {
			if (!this.isLoggedIn || this.isLoggingIn)
				return;
			if (this._projectUuid && this.acceptableProjects.map(p => p.uuid).includes(this._projectUuid))
				return;
			if (this.acceptableProjects.length > 0)
				this.selectAndSetCurrentProject();
		});
	}

	_clearAllData(includeNonUserData = false) {
		Object.values(stores)
			.filter(s => includeNonUserData || !(s instanceof AppConfigStore))
			.forEach(s => s._clear());
	}

	_refreshAllData(includeNonUserData = false) {
		Object.values(stores)
			.filter(s => includeNonUserData || !(s instanceof AppConfigStore))
			.forEach(s => s._refresh());
	}

	init(config: config, extra: extraConfig) {
		this.config = config;
		const {backend, sync, storage} = config;

		switch (backend) {
			case "api":
				this.backend = new ApiBackend();
				break;
			case "parent":
				if (extra.parentBackendRequestFn === undefined)
					console.error("PN Data: parentBackendRequestFn is required");
				else {
					// @ts-ignore - Yes, ParentBackend does implement the Backend interface
					this.backend = new ParentBackend(extra.parentBackendRequestFn);
				}
				break;
			case null:
				this.backend = new DummyBackend();
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/restrict-plus-operands
				console.error("Backend not recognized: " + backend);
		}

		if (this.sync)
			this.sync.destroy();

		switch (sync) {
			case "ws":
				this.sync = new WsSync();
				break;
			case "parent":
				this.sync = new ParentSync();
				break;
			case null:
				this.sync = null;
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/restrict-plus-operands
				console.error("Sync not recognized: " + sync);
		}

		if (this.sync) {
			setupSyncHandlers(this.sync);
			// TODO: fix this?
			// eslint-disable-next-line @typescript-eslint/unbound-method
			this.sync.setReconnectHandler(this._refreshAllData);
		}

		try {
			switch (storage) {
				case "extLocalStorage":
					this.storage = new ExtLocalStorage();
					break;
				case "mv2ExtLocalStorage":
					this.storage = new Mv2ExtLocalStorage();
					break;
				case "windowLocalStorage":
					this.storage = new WindowLocalStorage();
					break;
				case null:
					this.storage = null;
					break;
				default:
					// eslint-disable-next-line @typescript-eslint/restrict-plus-operands
					console.error("Storage not recognized: " + storage);
			}
		} catch (e) {
			console.warn("Error initializing storage: " + storage, e);
		}
		// Listen for any changes where the app needs to be notified and updated.
		this.storage?.listen(async (changes: StorageChange) => {
			for (const key in changes) {
				const {newValue, oldValue} = changes[key];

				switch (key) {
					case STORAGE_CURRENT_PROJECT_UUID:
						if (newValue !== oldValue) {
							await new Promise(r => setTimeout(r, 100 + Math.random() * 100));

							// The value might have changed in the last few milliseconds, so let's load it again.
							// This should help prevent some awful infinite loops.
							const projectUuid = (await this.storage?.getOne(STORAGE_CURRENT_PROJECT_UUID) ?? null) as projectUuid | null;

							// If the project is not in the list of acceptableProjects for the current user, ignore it.
							// This can happen if another tab has a different account logged in. In that case, it causes
							// infinite loops (each tab is trying to set a project that the other one doesn't see).
							if (!projectUuid || this.acceptableProjects.find(p => p.uuid === projectUuid))
								this.setCurrentProject(projectUuid);
						}
						break;
				}
			}
		});

		if (extra.outSyncHandler !== undefined)
			setupOutSyncHandlers(extra.outSyncHandler);

		if (extra.backendErrorHandler !== undefined)
			this.backendErrorHandler = extra.backendErrorHandler;

		if (extra.authFailHandler !== undefined)
			this._authFailHandler = extra.authFailHandler;

		// Set apiConfig
		Object.assign(apiConfig.urls, this.config.urls);
		apiConfig.client = this.config.client;
		apiConfig.version = this.config.version;
		apiConfig.authFailHandler = this.handleAuthFailure.bind(this);

		// Load app config from backend
		stores.appConfig._refresh();
	}

	login(accountUuid: accountUuid, accessToken: accessTokenUuid) {
		console.debug("PN Data: logging in (" + accountUuid + ")");

		if (accountUuid !== this.accountUuid && this._projectUuid)
			this.setCurrentProject(null);

		if (this.backend instanceof ApiBackend)
			apiConfig.accessToken = accessToken;

		this.accountUuid = accountUuid;
		this.accessToken = accessToken;
	}

	logout() {
		console.debug("PN Data: logging out");

		if (this.backend instanceof ApiBackend)
			apiConfig.accessToken = "";

		this.accountUuid = null;
		this.accessToken = null;
		this.setCurrentProject(null);
	}

	async createIntegrationAccount(integration: string, getClientToken: () => Promise<string>): Promise<void> {
		if (this.isLoggingIn)
			// Return a promise that resolves when isLoggingIn changes back to false (i.e. do nothing and make the caller wait)
			return waitUntil(() => !this.isLoggingIn);

		this.isLoggingIn = true;
		if (this.isLoggedIn)
			this.logout();

		const clientToken = await getClientToken();
		const {access_token, account, project} = await this.backend.createTempAccount(clientToken, integration);
		this.login(account.uuid, access_token);
		this.setCurrentProject(project.uuid);

		await waitUntil(() => this.me && this.project); // wait until data is loaded
		this.isLoggingIn = false;

		return undefined; // appease the typescript gods (consistent-return)
	}

	async integrationLogin(email: string, password: string, projectUuid: projectUuid | null) {
		if (!this.accountUuid || !this.isLoggedIn || !this.me?.integration)
			throw new Error("Not logged into an integration account.");
		if (this.me?.email)
			throw new Error("This account has already been upgraded.");

		const annotationCount = projectUuid ? stores.annotation.byProject(projectUuid).length : 0;

		this.isLoggingIn = true;
		const {
			account_uuid: accountUuid,
			access_token: accessToken,
			project_uuid: newProjectUuid
		} = await this.backend.integrationLogin(this.me.integration, this.accountUuid, email, password, projectUuid);

		if (this.isLoggedIn)
			this.logout(); // TODO: some bad requests are made here, logout needs to be improved. Things seem to work out though.

		this.login(accountUuid, accessToken);
		if (newProjectUuid)
			this.setCurrentProject(newProjectUuid);
		else
			await this.selectAndSetCurrentProject();

		this.isLoggingIn = false;

		if (annotationCount === 0 || !newProjectUuid) return;

		const refetchAnnotationIfNeeded = () => {
			// If the websocket update didn't come through, it might need to refetch annotations manually after a prudent time.
			const newAnnotations = stores.annotation.byProject(newProjectUuid);

			// If there are already all annotations, don't fetch them again.
			if (annotationCount === newAnnotations.length) return Promise.resolve(true);

			return stores.annotation._fetchByProject(newProjectUuid, true).then(() => {
				const annotations = stores.annotation.byProject(newProjectUuid);

				// If the annotations are still not there, try again.
				if (annotations.length < annotationCount)
					return false;

				// Set the current project again to trigger the outline and topics to load.
				this.setCurrentProject(newProjectUuid);

				return true;
			});
		};

		retryFunctionUntil(refetchAnnotationIfNeeded, isCompleted => isCompleted, {retries: 3}).catch(e => {
			console.log("DEBUG PN Data: failed to refetch annotations after logging in", e);
		});
	}

	async integrationSignup(name: string, email: string, password: string, terms: boolean) {
		if (!this.accountUuid || !this.isLoggedIn || !this.me?.integration)
			throw new Error("Not logged into an integration account.");
		if (this.me?.email)
			throw new Error("This account has already been upgraded.");

		await stores.account.integrationSignup(this.me.integration, this.accountUuid, name, email, password, terms);
	}

	getWebAppAuthLink(path: string): string {
		if (!this.isLoggedIn)
			throw new Error("Not logged in");

		const encodedAuthData = encodeURIComponent(JSON.stringify({
			"accessToken": this.accessToken,
			"account":     {"uuid": this.accountUuid}
		}));

		const encodedTarget = encodeURIComponent(this.config?.urls.pn_www + path);

		return `${this.config?.urls.pn_www}account/login-via-integration?auth_data=${encodedAuthData}&target=${encodedTarget}`;
	}

	/**
	 * Get the current project from storage if it exists.
	 * Otherwise, choose the first acceptable project available.
	 */
	async selectAndSetCurrentProject() {
		try {
			if (stores.project.all.length === 0)
				await waitUntil(() => stores.project.all.length > 0);

			const preferred = await this.storage?.getOne(STORAGE_CURRENT_PROJECT_UUID);

			let elected;
			if (this.acceptableProjects.length === 0)
				elected = null; // none available
			else if (preferred && this.acceptableProjects.map(p => p.uuid).includes(preferred))
				elected = preferred;
			else
				elected = this.acceptableProjects[0].uuid;

			this.setCurrentProject(elected);
		} catch (error) {
			console.error(error);
		}
	}

	setCurrentProject(projectUuid: projectUuid | null) {
		this._projectUuid = projectUuid;

		// This triggers loading the project data (outline, topics, annotations)
		if (projectUuid)
			stores.outline.getMergedByProject(projectUuid);

		// Update currently active/selected project. Do this after a short pause to prevent infinite loop situations.
		(async () => {
			await new Promise(r => setTimeout(r, 100 + Math.random() * 100));
			const projectUuid = this._projectUuid;

			/**
			 * Only ever set STORAGE_CURRENT_PROJECT_UUID, and don't clear it.
			 * Otherwise it can be lost if the app is disabled (current project
			 * becomes null) and later re-enabled.
			 *
			 * If STORAGE_CURRENT_PROJECT_UUID couldn't be found,
			 * selectAndSetCurrentProject() will call this function and update
			 * it to a new uuid anyway.
			 */
			if (projectUuid)
				this.storage?.setOne(STORAGE_CURRENT_PROJECT_UUID, projectUuid);
		})();
	}

	setCurrentPage(url: string) {
		this.pageUrl = url;
	}

	clearCurrentPage() {
		this.pageUrl = null;
	}

	handleBackendError(error: any, ctx: any) {
		if (this.backendErrorHandler)
			this.backendErrorHandler(error, ctx);
	}

	handleAuthFailure() {
		console.warn("PN Data: logging out because of authentication failure");

		if (this._authFailHandler)
			this._authFailHandler();

		this.logout();
	}

	get isLoggedIn(): boolean {
		return !!(this.accountUuid && this.accessToken);
	}

	get me(): account | undefined {
		return stores.account.me;
	}

	get project(): project | undefined {
		if (!this._projectUuid)
			return undefined;

		return stores.project.byUuid(this._projectUuid);
	}

	get outline(): mergedOutline2 | undefined {
		if (!this._projectUuid)
			return undefined;

		return stores.outline.getMergedByProject(this._projectUuid);
	}

	get topics(): topic[] {
		if (!this._projectUuid)
			return [];

		return stores.topic.byProject(this._projectUuid);
	}

	get notifications(): notification[] {
		return stores.notification.all;
	}

	get pageHighlights(): Record<annotationUuid, {annotation: annotation; topic: topic}> {
		if (!this.project || !this.pageUrl)
			return {};

		const annotations = stores.annotation.byProject(this.project.uuid)
			.filter(a =>
				a.type === "highlight"
				&& a.pageData?.pageUrl === this.pageUrl
			);

		return annotations.reduce((acc: Record<annotationUuid, {annotation: annotation; topic: topic}>, annotation) => {
			if (!this.project)
				return acc;
			const topicUuid = stores.outline.getTopicUuidForAnnotation(annotation.uuid, this.project.uuid);
			if (!topicUuid)
				return acc;
			const topic = stores.topic.byUuid(topicUuid);
			if (!topic)
				return acc;
			acc[annotation.uuid] = {annotation, topic};
			return acc;
		}, {});
	}

	/**
	 * Determines whether the current page is bookmarked
	 * and returns the annotation UUID for the bookmark.
	 **/
	get bookmarkUuid(): annotationUuid | null {
		if (!this.pageUrl)
			return null;

		let uuid: annotationUuid | null = null;

		this.outline
			?.find(ot => ot.annotations?.find(
				a => {
					if (
						a.type === "bookmark"
						&& a.pageData
						&& a.pageData.pageUrl === Data.app.pageUrl
					) {
						uuid = a.uuid;
						return true;
					} else
						return false;
				}
			));

		return uuid;
	};

	get hasBackend(): boolean {
		return !(this.backend instanceof DummyBackend);
	}

	get hasStorage(): boolean {
		return this.storage !== null;
	}

	// Active projects where the current user has edit access.
	get acceptableProjects(): project[] {
		const me = this.me;
		if (!this.isLoggedIn || !me)
			return [];

		let f: (p: project) => boolean;
		if (this.config?.settings?.showReadOnlyProjects ?? true)
			f = p => p.status !== "archived";
		else {
			f = p => p.status !== "archived"
				&& Project.hasAccess(me, p, Project.ACCESS_EDIT);
		}

		return stores.project.all.filter(f);
	}

	unmarkHighlightAsUnrenderable(uuid: annotationUuid) {
		if (!this.unrenderableHighlights.includes(uuid))
			return;
		this.unrenderableHighlights.splice(this.unrenderableHighlights.indexOf(uuid), 1);
	}

	markHighlightAsUnrenderable(uuid: annotationUuid) {
		if (this.unrenderableHighlights.includes(uuid))
			return;
		this.unrenderableHighlights.push(uuid);
	}

	setRenderedHighlights(successful: annotationUuid[], failed: annotationUuid[]) {
		successful
			.filter(uuid => this.unrenderableHighlights.includes(uuid))
			.forEach(uuid => this.unrenderableHighlights.splice(this.unrenderableHighlights.indexOf(uuid), 1));

		failed
			.filter(uuid => !this.unrenderableHighlights.includes(uuid))
			.forEach(uuid => this.unrenderableHighlights.push(uuid));
	}
}

export default new App();
